import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { OpenRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { useApi } from "@/hooks/useApi";
import { HoursApi } from "@/services/api/hours-api";
import { useAuth } from "@/services/auth/use-auth";
import { DedicatedHoursForm } from "./DedicatedHoursForm";
import { DynamicHoursForm } from "./DynamicHoursForm";
import { EnergyStafferHoursForm } from "./EnergyStafferHoursForm";
import { formatWeekRange } from "@/utils/calendar-date";
import { resolveLaunchUrl } from "@/utils/launch-url";
import { resolveWeeklyHoursForms } from "./weekly-hours-access";
import type { WeeklyHoursFormDefinition, WeeklyHoursFormKind } from "./weekly-hours-types";
import "./WeeklyHours.scss";

function isDedicated(kind: WeeklyHoursFormKind): kind is "energy" | "energy-si" | "technology" | "power" {
  return kind === "energy" || kind === "energy-si" || kind === "technology" || kind === "power";
}

function isDynamic(kind: WeeklyHoursFormKind): kind is "cme" | "real-estate" | "consumer-retail" | "gpc" {
  return kind === "cme" || kind === "real-estate" || kind === "consumer-retail" || kind === "gpc";
}

function WeeklyHoursForm({ form, userId }: { form: WeeklyHoursFormDefinition; userId?: number }): JSX.Element {
  if (isDedicated(form.kind)) return <DedicatedHoursForm kind={form.kind} />;
  if (isDynamic(form.kind)) return <DynamicHoursForm kind={form.kind} currentUserId={userId} />;
  return <EnergyStafferHoursForm isSi={form.kind === "energy-si-staffer"} actorUserId={userId} />;
}

export function WeeklyHours(): JSX.Element {
  const { user } = useAuth();
  const fetcher = useCallback(() => HoursApi.getUserSubDepartments(), []);
  const { data, loading, error, refetch } = useApi(fetcher);
  const forms = useMemo(() => resolveWeeklyHoursForms(data ?? []), [data]);
  const [selectedKind, setSelectedKind] = useState<WeeklyHoursFormKind | null>(null);
  const dashboardUrl = resolveLaunchUrl("dashboard");

  useEffect(() => {
    if (forms.length === 0) {
      setSelectedKind(null);
      return;
    }
    setSelectedKind((current) => (current && forms.some((form) => form.kind === current) ? current : forms[0].kind));
  }, [forms]);

  const selectedForm = forms.find((form) => form.kind === selectedKind) ?? null;

  if (loading) {
    return (
      <div className="weekly-hours__state" role="status">
        <FluentSpinner label="Checking your dashboard access…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="weekly-hours__state" role="alert">
        <p>{error}</p>
        <button type="button" className="weekly-hours__secondary-button" onClick={() => void refetch()}>
          Try again
        </button>
      </div>
    );
  }

  if (!selectedForm) {
    return (
      <div className="weekly-hours__empty weekly-hours__empty--page">
        <h3>No dashboard hour-entry forms</h3>
        <p>
          Your current TRIYO access does not include an editable GIB dashboard time log. FIG and M&amp;A hours remain on
          their task edit pages; other staffer and group-head dashboards are read-only.
        </p>
        {dashboardUrl ? (
          <a href={dashboardUrl} target="_blank" rel="noreferrer" className="weekly-hours__web-link">
            Open TRIYO dashboard <OpenRegular />
          </a>
        ) : null}
      </div>
    );
  }

  return (
    <div className="weekly-hours">
      <div className="weekly-hours__context">
        <div>
          <span>Week of</span>
          <strong>{formatWeekRange(new Date())}</strong>
        </div>
        <span className="weekly-hours__form-count">
          {forms.length} form{forms.length === 1 ? "" : "s"}
        </span>
      </div>

      {forms.length > 1 ? (
        <label className="weekly-hours__form-select">
          <span>Hours form</span>
          <select
            value={selectedForm.kind}
            onChange={(event) => setSelectedKind(event.target.value as WeeklyHoursFormKind)}
          >
            {forms.map((form) => (
              <option key={form.kind} value={form.kind}>
                {form.label}
              </option>
            ))}
          </select>
          <small>{selectedForm.description}</small>
        </label>
      ) : null}

      <WeeklyHoursForm key={selectedForm.kind} form={selectedForm} userId={user?.Id} />

      {dashboardUrl ? (
        <a
          href={dashboardUrl}
          target="_blank"
          rel="noreferrer"
          className="weekly-hours__web-link weekly-hours__web-link--footer"
        >
          Open full dashboard <OpenRegular />
        </a>
      ) : null}
    </div>
  );
}
